import { type ReactNode, useId } from "react";
import type { Item } from "./api.ts";
import { Icon } from "./souvenir.ts";

// What a place or a stay is like, as a hotel site would tell you, on its card's sections (DESIGN.md, "A card reads in
// sections"): Getting there (getting around from it, what's nearby) and What it's like (its own facts, pros and cons,
// amenities worth knowing, and what's good to know). Its address is At a glance, never again here. Options shows the
// gist.

export function travel(how: string | undefined, minutes: number | undefined): string {
  const mode = how?.trim() || "walk";
  if (minutes === undefined) return mode === "walk" ? "" : mode;
  return mode === "walk" ? `${minutes} min walk` : `${minutes} min by ${mode}`;
}

export const hasGettingThere = (item: Item) => Boolean(item.getting_around?.length || item.nearby?.length);

// A place's own facts here: how long it takes and the best time (its hours are At a glance, whether it needs booking
// under Booking).
const placeFacts = (item: Item): { icon: "route" | "sunrise"; text: string }[] => [
  ...(item.duration ? [{ icon: "route" as const, text: item.duration }] : []),
  ...(item.best_time ? [{ icon: "sunrise" as const, text: item.best_time }] : []),
];

export const hasWhatItsLike = (item: Item) =>
  Boolean(
    placeFacts(item).length ||
      item.pros?.length ||
      item.cons?.length ||
      item.amenities?.length ||
      item.good_to_know?.length,
  );

// One part of a section with a label of its own, under the section's heading ("Nearby", "Good to know"), named by it.
function Part({ label, children }: { label: string; children: ReactNode }) {
  const id = useId();
  return (
    <section className="tm-stay-part" aria-labelledby={id}>
      <h3 id={id} className="tm-settings-label tm-stay-title">
        {label}
      </h3>
      {children}
    </section>
  );
}

export function GettingThere({ item }: { item: Item }) {
  return (
    <div className="tm-stay-info">
      {item.getting_around && item.getting_around.length > 0 && (
        <Part label="Getting around">
          <ul className="tm-stay-rows">
            {item.getting_around.map((g) => (
              <li key={`${g.to}-${g.how ?? ""}`} className="tm-stay-item">
                <span className="tm-stay-row">
                  <span className="callout tm-stay-what">{g.to}</span>
                  <span className="callout tm-muted tm-stay-how">{travel(g.how, g.minutes)}</span>
                </span>
                {g.note && <span className="caption tm-muted">{g.note}</span>}
              </li>
            ))}
          </ul>
        </Part>
      )}
      {item.nearby && item.nearby.length > 0 && (
        <Part label="Nearby">
          <ul className="tm-stay-rows">
            {item.nearby.map((n) => (
              <li key={n.name} className="tm-stay-item">
                <span className="tm-stay-row">
                  <span className="callout tm-stay-what">
                    {n.name}
                    {n.what && <span className="tm-muted"> · {n.what}</span>}
                  </span>
                  {n.minutes !== undefined && <span className="callout tm-muted tm-stay-how">{n.minutes} min</span>}
                </span>
                {n.note && <span className="caption tm-muted">{n.note}</span>}
              </li>
            ))}
          </ul>
        </Part>
      )}
    </div>
  );
}

export function WhatItsLike({ item }: { item: Item }) {
  const facts = placeFacts(item);
  return (
    <div className="tm-stay-info">
      {facts.length > 0 && (
        <ul className="tm-facts">
          {facts.map((f) => (
            <li key={f.icon} className="callout">
              <Icon name={f.icon} size={20} />
              <span>{f.text}</span>
            </li>
          ))}
        </ul>
      )}
      {/* An option's pros and cons in full: its tile shows their start (Options.tsx). */}
      {Boolean(item.pros?.length || item.cons?.length) && (
        <ul className="tm-proscons" aria-label="Pros and cons">
          {item.pros?.map((p) => (
            <li key={`+${p}`} className="callout is-pro">
              <Icon name="check" size={20} />
              <span>{p}</span>
            </li>
          ))}
          {item.cons?.map((c) => (
            <li key={`-${c}`} className="callout is-con">
              <Icon name="close" size={20} />
              <span>{c}</span>
            </li>
          ))}
        </ul>
      )}
      {item.amenities && item.amenities.length > 0 && (
        <Part label="Amenities">
          <ul className="tm-tags">
            {item.amenities.map((a) => (
              <li key={a} className="caption tm-tag">
                {a}
              </li>
            ))}
          </ul>
        </Part>
      )}
      {item.good_to_know && item.good_to_know.length > 0 && (
        <Part label="Good to know">
          <ul className="tm-rich-block tm-rich-list callout">
            {item.good_to_know.map((g) => (
              <li key={g}>{g}</li>
            ))}
          </ul>
        </Part>
      )}
    </div>
  );
}
